// panels/pvc/PvcHome.js —— 光储充 · 3.1 站点首页（需求文档 p2–p4）
// 能耗收益：近七日总电量（电网/负载/光伏）、近七日储能电量（柱形图）、收益曲线
// 电站信息：站点名称/位置/投运时间/光伏与储能装机/站点照片 + 设备状态
// 能量流转图：市电（总表）/光伏（含出力百分比）/储能（充放电状态）/充电桩（含详情）/负载  —— 差异 #4
// 运行曲线：实时数据（实线）+ 预测数据（虚线，每日 18:00 更新）—— 差异 #2
// 差异 #1：无「系统效率」，改用「系统收益 = 负载表电量×电网电价 − 电网总表电量×电网电价」，
//          另有「光伏收益参考」「储能收益参考」。
import React, { useRef, useState } from 'react';
import { Tag, Button, Modal, Segmented } from 'antd';
import {
  ResponsiveContainer, ComposedChart, LineChart, BarChart,
  Area, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine,
} from 'recharts';
import {
  FundOutlined, SunOutlined, DatabaseOutlined, ThunderboltOutlined, EyeOutlined,
  DownOutlined, UpOutlined,
} from '@ant-design/icons';
import { getOverview, getDevices } from '../../../../config/stationData';
import { Ef2NodeCard, ef2Pct } from '../../../../components/TopoGlbIcon';
import {
  PV_INSTALL_KW, getSevenDayEnergy, getSevenDayIncome, sumField,
  getRealtimeCurve, getForecastCurve,
} from './data';
import './pvc.css';

const AXIS = { fill: 'rgba(255,255,255,0.5)', fontSize: 11 };
const TT = { background: '#0d1f44', border: '1px solid rgba(0,180,216,0.35)', borderRadius: 8, color: '#fff' };
const GRID = 'rgba(255,255,255,0.08)';
const DASH = '5 4';

const Kpi = ({ label, value, unit, icon, foot }) => (
  <div className="kpi-card">
    {icon ? <div className="kpi-icon">{icon}</div> : null}
    <div className="kpi-label">{label}</div>
    <div className="kpi-value">{value}<span className="unit">{unit}</span></div>
    {foot ? <div className="kpi-foot">{foot}</div> : null}
  </div>
);

// 能量流转：母线/连线仍 SVG；节点卡 HTML 覆盖，右侧接 GLB（变压器暂留空）
const EF_W = 900;
const EF_H = 300;
const EnergyFlow = ({ grid, storage, storageState, pv, pvPct, charger, load, transformer }) => {
  const nodes = [
    { name: '储能', val: storage, unit: 'kW', color: storageState === '充电' ? '#00d4ff' : '#52e8b0', sub: storageState, up: storageState !== '充电', device: 'storage' },
    { name: '光伏', val: pv, unit: 'kW', color: '#f4c542', sub: `出力 ${pvPct}%`, up: true, device: 'pv' },
    { name: '充电桩', val: charger, unit: 'kW', color: '#00d4ff', sub: '充电负荷', up: true, device: 'charger' },
    { name: '负载', val: load, unit: 'kW', color: '#ff5a7f', sub: '用电负荷', up: false, device: 'load' },
  ];
  const cx = EF_W / 2;
  const busY = 168;
  const boxY = 210;
  const boxH = 74;
  const boxW = 148;
  const left = 130;
  const right = EF_W - 130;
  const step = (right - left) / (nodes.length - 1);
  const importing = grid >= 0;

  return (
    <div className="ef2-wrap">
      <svg className="ef2-svg" viewBox={`0 0 ${EF_W} ${EF_H}`} role="img" aria-label="能量流转示意">
        <defs>
          <marker id="pf-down" markerWidth="9" markerHeight="9" refX="7" refY="4.5" orient="auto"><path d="M0,0 L9,4.5 L0,9 z" fill="#00d4ff" /></marker>
          <marker id="pf-up" markerWidth="9" markerHeight="9" refX="7" refY="4.5" orient="auto"><path d="M0,0 L9,4.5 L0,9 z" fill="#52e8b0" /></marker>
        </defs>
        <line className="ef2-link" x1={cx} y1={66} x2={cx} y2={90} markerEnd={`url(#pf-${importing ? 'down' : 'up'})`} />
        <line className="ef2-link" x1={cx} y1={134} x2={cx} y2={busY} markerEnd="url(#pf-down)" />
        <text className="ef2-node-sub" x={cx + 14} y={158} textAnchor="start">{transformer}</text>
        <line className="ef2-bus" x1={60} y1={busY} x2={EF_W - 60} y2={busY} />
        <text className="ef2-bus-label" x={60} y={busY - 12}>交流母线 0.4 kV</text>
        {nodes.map((n, i) => {
          const x = left + step * i;
          return (
            <g key={n.name}>
              <circle cx={x} cy={busY} r={4.5} fill="#00d4ff" />
              <line className="ef2-link" x1={x} y1={n.up ? boxY : busY} x2={x} y2={n.up ? busY : boxY} markerEnd={`url(#pf-${n.up ? 'up' : 'down'})`} />
            </g>
          );
        })}
      </svg>
      <Ef2NodeCard
        name="市电（总表）"
        value={grid}
        unit="kW"
        sub={importing ? '受电' : '反送'}
        valueColor={importing ? '#00d4ff' : '#52e8b0'}
        device="grid"
        style={ef2Pct(cx - 104, 8, 208, 58)}
      />
      <Ef2NodeCard
        name="变压器"
        sub={transformer}
        device={null}
        style={ef2Pct(cx - 82, 90, 164, 44)}
      />
      {nodes.map((n, i) => {
        const x = left + step * i;
        return (
          <Ef2NodeCard
            key={n.name}
            name={n.name}
            value={n.val}
            unit={n.unit}
            sub={n.sub}
            valueColor={n.color}
            device={n.device}
            style={ef2Pct(x - boxW / 2, boxY, boxW, boxH)}
          />
        );
      })}
    </div>
  );
};

const PvcHome = ({ station }) => {
  const [showChargerDetail, setShowChargerDetail] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [dayView, setDayView] = useState('today');
  const chartRef = useRef(null);

  const ov = getOverview(station.id);
  const dev = getDevices(station.id);

  // 总表（市电）/ 负载表 / 光伏表取自电表
  const gridMeter = dev.meters[0];
  const loadMeter = dev.meters.find((m) => m.name.includes('负荷')) || dev.meters[dev.meters.length - 1];
  const pvMeter = dev.meters.find((m) => m.name.includes('光伏'));
  const gridPower = gridMeter ? gridMeter.p : 0;
  const loadPower = loadMeter ? loadMeter.p : ov.loadPower;
  const pvPower = pvMeter ? pvMeter.p : ov.pvPower;
  const pvPct = Math.round((pvPower / (PV_INSTALL_KW[station.id] || 400)) * 100);
  const chargerPower = dev.charger.reduce((s, c) => s + (c.power || 0), 0);

  // 能耗收益 / 收益
  const energy7 = getSevenDayEnergy(station.id);
  const income7 = getSevenDayIncome(station.id);
  const systemIncome = sumField(income7, 'system');
  const pvIncome = sumField(income7, 'pv');
  const storageIncome = sumField(income7, 'storage');
  const gridTotal = sumField(energy7, 'grid');
  const loadTotal = sumField(energy7, 'load');
  const pvTotal = sumField(energy7, 'pv');

  // 运行曲线（实时 / 预测）
  const realtime = getRealtimeCurve(station.id);
  const dayCurve = dayView === 'yesterday'
    ? realtime.map((d) => ({ ...d, grid: Math.round(d.grid * 0.94), pv: Math.round(d.pv * 1.06), charger: Math.round(d.charger * 0.92) }))
    : realtime;
  const forecast = getForecastCurve(station.id);

  const info = [
    ['站点名称', station.name],
    ['站点位置', station.location],
    ['投运时间', station.onlineAt],
    ['光伏装机功率', station.has.pv ? `${(PV_INSTALL_KW[station.id] || 400) / 1000} MW` : '无'],
    ['储能装机功率', station.rated.power],
    ['储能装机容量', station.rated.capacity],
  ];

  const devStatusGroups = [
    { name: '电表', rows: dev.meters.map((m) => [m.name, m.state]) },
    { name: '光伏逆变器', rows: dev.pv.map((p) => [p.name, p.state]) },
    { name: 'PCS', rows: dev.pcs.map((p) => [p.name, p.alarm || (p.running ? '正常' : '停机')]) },
    { name: 'BMS', rows: dev.racks.map((r) => [r.name, r.alarm || '正常']) },
    { name: '温控', rows: [...dev.hvac.map((h) => [h.name, h.alarm || h.state]), [dev.liquid.name, dev.liquid.state]] },
    { name: '充电桩', rows: dev.charger.map((c) => [c.name, c.state === '离线' ? '离线' : '正常']) },
  ];

  const realtimeChart = (height) => (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={dayCurve} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
        <defs>
          <linearGradient id="pfSoc" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#8b7cf6" stopOpacity={0.5} />
            <stop offset="95%" stopColor="#8b7cf6" stopOpacity={0.03} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
        <XAxis dataKey="clock" interval={11} tick={AXIS} axisLine={false} tickLine={false} />
        <YAxis yAxisId="left" tick={AXIS} axisLine={false} tickLine={false} />
        <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tick={AXIS} axisLine={false} tickLine={false} width={38} />
        <Tooltip contentStyle={TT} />
        <Legend wrapperStyle={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }} iconSize={8} />
        <ReferenceLine yAxisId="left" y={0} stroke="rgba(255,255,255,0.35)" />
        <ReferenceLine yAxisId="left" y={dayCurve[0].demandLimit} stroke="#f4c542" strokeDasharray={DASH}
          label={{ value: `需量限制 ${dayCurve[0].demandLimit} kW`, fill: '#f4c542', fontSize: 11, position: 'insideTopRight' }} />
        <Line yAxisId="left" type="stepAfter" dataKey="grid" name="电网功率" stroke="#4dd8ff" dot={false} strokeWidth={2} />
        <Line yAxisId="left" type="stepAfter" dataKey="storage" name="储能功率" stroke="#52e8b0" dot={false} strokeWidth={2} />
        <Line yAxisId="left" type="stepAfter" dataKey="pv" name="光伏功率" stroke="#f4c542" dot={false} strokeWidth={2} />
        <Line yAxisId="left" type="stepAfter" dataKey="charger" name="充电桩功率" stroke="#00d4ff" dot={false} strokeWidth={2} />
        <Area yAxisId="right" type="linear" dataKey="soc" name="SOC %" stroke="#8b7cf6" fill="url(#pfSoc)" strokeWidth={2} baseValue={0} />
      </ComposedChart>
    </ResponsiveContainer>
  );

  const forecastChart = (height) => (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={forecast} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
        <XAxis dataKey="clock" interval={11} tick={AXIS} axisLine={false} tickLine={false} />
        <YAxis tick={AXIS} axisLine={false} tickLine={false} />
        <Tooltip contentStyle={TT} />
        <Legend wrapperStyle={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }} iconSize={8} />
        <ReferenceLine y={0} stroke="rgba(255,255,255,0.35)" />
        <Line type="stepAfter" dataKey="strategyPower" name="储能策略功率（计划曲线）" stroke="#00d4ff" strokeDasharray={DASH} dot={false} strokeWidth={2} />
        <Line type="stepAfter" dataKey="pvForecast" name="光伏预测" stroke="#f4c542" strokeDasharray={DASH} dot={false} strokeWidth={2} />
        <Line type="stepAfter" dataKey="loadForecast" name="负载综合预测" stroke="#ff5a7f" strokeDasharray={DASH} dot={false} strokeWidth={2} />
      </LineChart>
    </ResponsiveContainer>
  );

  return (
    <div>
      {/* 收益 KPI（差异 #1：无系统效率） */}
      <div className="kpi-grid">
        <Kpi label="系统收益（近七日）" value={systemIncome.toLocaleString()} unit="元" icon={<FundOutlined />} foot="负载表电量×电网电价 − 电网总表电量×电网电价" />
        <Kpi label="光伏收益参考" value={pvIncome.toLocaleString()} unit="元" icon={<SunOutlined />} foot="光伏发电量×光伏发电电价 · 仅供参考" />
        <Kpi label="储能收益参考" value={storageIncome.toLocaleString()} unit="元" icon={<DatabaseOutlined />} foot="储能放电量×电网电价 − 储能充电量×电网电价" />
        <Kpi label="近七日光伏发电量" value={pvTotal.toLocaleString()} unit="kWh" icon={<ThunderboltOutlined />} foot={`电网 ${gridTotal.toLocaleString()} · 负载 ${loadTotal.toLocaleString()} kWh`} />
      </div>

      {/* 能耗收益 */}
      <div className="grid-2-even">
        <div className="panel">
          <div className="panel-title">近七日总电量<small>电网 / 负载 / 光伏 · 最近 7 天（不含今天）· kWh</small></div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={energy7} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
              <XAxis dataKey="day" tick={AXIS} axisLine={false} tickLine={false} />
              <YAxis tick={AXIS} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={TT} />
              <Legend wrapperStyle={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }} iconSize={8} />
              <Bar dataKey="grid" name="电网电量" fill="#4dd8ff" barSize={14} radius={[4, 4, 0, 0]} />
              <Bar dataKey="load" name="负载电量" fill="#ff5a7f" barSize={14} radius={[4, 4, 0, 0]} />
              <Bar dataKey="pv" name="光伏电量" fill="#f4c542" barSize={14} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="panel">
          <div className="panel-title">近七日储能电量<small>每日充 / 放电量 · 最近 7 天（不含今天）· kWh</small></div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={energy7} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
              <XAxis dataKey="day" tick={AXIS} axisLine={false} tickLine={false} />
              <YAxis tick={AXIS} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={TT} />
              <Legend wrapperStyle={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }} iconSize={8} />
              <Bar dataKey="charge" name="充电量" fill="#00d4ff" barSize={16} radius={[4, 4, 0, 0]} />
              <Bar dataKey="discharge" name="放电量" fill="#52e8b0" barSize={16} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="panel">
        <div className="panel-title">收益曲线<small>系统收益 / 光伏收益参考 / 储能收益参考 · 最近 7 天（不含今天）· 元</small></div>
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={income7} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
            <XAxis dataKey="day" tick={AXIS} axisLine={false} tickLine={false} />
            <YAxis tick={AXIS} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={TT} />
            <Legend wrapperStyle={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }} iconSize={8} />
            <ReferenceLine y={0} stroke="rgba(255,255,255,0.35)" />
            <Line type="linear" dataKey="system" name="系统收益" stroke="#52e8b0" dot strokeWidth={2} />
            <Line type="linear" dataKey="pv" name="光伏收益参考" stroke="#f4c542" dot strokeWidth={2} />
            <Line type="linear" dataKey="storage" name="储能收益参考" stroke="#4dd8ff" dot strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* 能量流转图（差异 #4：充电桩 + 光伏 + 负载） */}
      <div className="panel">
        <div className="panel-title">
          <span>能量流转<small style={{ marginLeft: 8 }}>即时功率方向 · 市电（总表）/ 光伏 / 储能 / 充电桩 / 负载</small></span>
          <Button size="small" className="panel-title-btn" icon={showChargerDetail ? <UpOutlined /> : <DownOutlined />} onClick={() => setShowChargerDetail((v) => !v)}>
            充电桩详情
          </Button>
        </div>
        <div className="ef2-panel-body">
          <EnergyFlow
            grid={gridPower}
            storage={ov.storagePower}
            storageState={ov.storageState}
            pv={pvPower}
            pvPct={pvPct}
            charger={chargerPower}
            load={loadPower}
            transformer={station.grid.transformer}
          />
          <div className="dev-kv ef2-kv">
            <div className="dev-kv-row"><span className="dev-kv-k">市电功率</span><span className="dev-kv-v">{gridPower}<span className="unit">kW</span></span></div>
            <div className="dev-kv-row"><span className="dev-kv-k">光伏出力</span><span className="dev-kv-v">{pvPower}<span className="unit">kW</span> · {pvPct}%</span></div>
            <div className="dev-kv-row"><span className="dev-kv-k">储能状态</span><span className="dev-kv-v">{ov.storageState} · {ov.storagePower} kW</span></div>
            <div className="dev-kv-row"><span className="dev-kv-k">充电桩功率</span><span className="dev-kv-v">{chargerPower}<span className="unit">kW</span></span></div>
            <div className="dev-kv-row"><span className="dev-kv-k">负载总功率</span><span className="dev-kv-v">{loadPower}<span className="unit">kW</span></span></div>
            <div className="dev-kv-row"><span className="dev-kv-k">SOC / 可用电量</span><span className="dev-kv-v">{ov.soc}% · {ov.availableEnergy} kWh</span></div>
          </div>
          {showChargerDetail ? (
            <div className="pvc-charger-detail">
              {dev.charger.map((c) => (
                <div className="pvc-charger-row" key={c.id}>
                  <span>{c.name}　<span style={{ color: 'rgba(255,255,255,0.45)' }}>{c.collect}</span></span>
                  <span style={{ color: c.power ? '#00d4ff' : 'rgba(255,255,255,0.5)' }}>{c.power ? `${c.power} kW` : '—'}</span>
                </div>
              ))}
              <div className="pvc-charger-row" style={{ borderTop: '1px solid rgba(255,255,255,0.12)', marginTop: 4, paddingTop: 6 }}>
                <span>充电桩功率合计</span><span style={{ color: '#00d4ff', fontWeight: 600 }}>{chargerPower} kW</span>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* 电站信息 + 设备状态 */}
      <div className="grid-2">
        <div className="panel">
          <div className="panel-title">运行曲线<small>实时数据 + 预测数据（虚线，每日 18:00 更新）</small>
            <Button size="small" className="panel-title-btn" icon={<EyeOutlined />} onClick={() => { setDayView('today'); setModalOpen(true); }}>放大</Button>
          </div>
          {realtimeChart(240)}
          <div style={{ marginTop: 16 }}>{forecastChart(200)}</div>
        </div>

        <div className="panel">
          <div className="panel-title">电站信息<small>{station.typeLabel}</small></div>
          <div className="pvc-photo">站点照片（演示占位）</div>
          <div className="dev-kv">
            {info.map(([k, v]) => (
              <div className="dev-kv-row" key={k}><span className="dev-kv-k">{k}</span><span className="dev-kv-v">{v}</span></div>
            ))}
          </div>
          <div className="dev-sub">
            <Tag color={station.comm === 'normal' ? 'green' : station.comm === 'delay' ? 'orange' : 'red'}>
              {station.comm === 'normal' ? '通信正常' : station.comm === 'delay' ? '通信延迟' : '通信离线'}
            </Tag>
            <Tag color={station.strategyOn ? 'cyan' : 'default'}>{station.strategyOn ? '计划曲线策略已启用' : '策略未启用'}</Tag>
          </div>
        </div>
      </div>

      {/* 设备状态总览（电站信息：正常 / 异常文字） */}
      <div className="panel">
        <div className="panel-title">设备状态<small>正常显示「正常」，异常时显示对应异常 · 请及时查看告警并检查设备</small></div>
        <div className="dev-status-grid dev-status-overview">
          {devStatusGroups.map((g) => {
            const abnormal = g.rows.filter(([, s]) => !['正常', '运行', '停机', '待机', '充电中'].includes(s));
            return (
              <div className={`dev-card${abnormal.length ? ' fault' : ''}`} key={g.name}>
                <div className="dev-card-head">
                  <span className="dev-name">{g.name}</span>
                  <span className="dev-status"><span className={`dot ${abnormal.length ? 'fault' : 'online'}`} />{abnormal.length ? '异常' : '正常'}</span>
                </div>
                <div className="dev-kv">
                  {g.rows.map(([n, s]) => (
                    <div className="dev-kv-row" key={n}>
                      <span className="dev-kv-k">{n}</span>
                      <span className="dev-kv-v" style={{ color: ['正常', '运行', '停机', '待机', '充电中'].includes(s) ? 'rgba(255,255,255,0.78)' : '#ff5a7f' }}>{s}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Modal
        title="运行曲线 · 按天切换"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={960}
        destroyOnHidden
      >
        <Segmented
          value={dayView}
          onChange={setDayView}
          style={{ marginBottom: 12 }}
          options={[{ value: 'yesterday', label: '昨日' }, { value: 'today', label: '今日' }, { value: 'tomorrow', label: '明日（预测）' }]}
        />
        <div className="panel" style={{ marginBottom: 16 }} ref={chartRef}>
          <div className="panel-title">实时数据<small>电网功率 / 储能功率 / SOC / 光伏功率 / 充电桩功率 / 需量限制</small></div>
          {dayView === 'tomorrow' ? forecastChart(300) : realtimeChart(300)}
        </div>
        <div className="panel">
          <div className="panel-title">预测数据<small>储能策略功率（计划曲线）/ 光伏预测 / 负载综合预测 · 虚线 · 每日 18:00 更新</small></div>
          {forecastChart(260)}
        </div>
      </Modal>
    </div>
  );
};

export default PvcHome;
